import { PlanName } from '../helpers/billing-helper'

/**
 * Subscription tier configuration interface
 */
export interface SubscriptionTier {
  planName: PlanName
  name: string
  price: string
  tag: string
  description: string
  features: string[]
  lockMonths: number // Months until rewards unlock
  monthlyCapDollars: number // Monthly redemption cap in dollars
  monthlyGoalPoints: number // Points required per month to unlock reward (tier-specific)
  payoutCadence: 'monthly' | 'every_2_months' // Payout frequency after first payout
  rolloverPointsCap?: number // Maximum points that can roll over (VIP only)
  rolloverApplies?: boolean // Whether rollover applies to this tier (VIP only)
}

/**
 * Centralized subscription tier definitions
 * Updated: New tier model with Free, Explorer, VIP
 * - Free: $0, unlocks after 6 months, $5/month cap, 20,000 points/month goal, monthly payout
 * - Explorer: $7.99/month, unlocks after 3 months, first payout month 4, then $10 every 2 months, 15,000 points/month goal
 * - VIP: $14.99/month, unlocks after 2 months, first payout month 3, then $20 every 2 months, 10,000 points/month goal, rollover up to 2,000 points
 * 
 * This is the single source of truth for tier configuration.
 * All UI surfaces (cards, progress module, messaging) use values from this config.
 */
export const SUBSCRIPTION_TIERS: SubscriptionTier[] = [
  {
    planName: 'free',
    name: 'Free',
    price: '$0',
    tag: 'Get started',
    description: 'Start earning points and unlock rewards after building your activity history.',
    features: [
      'Earn points on menu scans, reviews, and invites',
      'Rewards unlock after 6 months of activity',
      'Redeem up to $5/month in rewards',
      'Earn 20,000 points/month to unlock credits',
    ],
    lockMonths: 6,
    monthlyCapDollars: 5,
    monthlyGoalPoints: 20000,
    payoutCadence: 'monthly',
  },
  {
    planName: 'explorer',
    name: 'Explorer',
    price: '$7.99 / month',
    tag: 'Most popular',
    description: 'Perfect if you dine out or order in a few times a week.',
    features: [
      'Everything in Free',
      'Rewards unlock after 3 months of activity',
      'Earn up to $10 every 2 months',
      'Earn 3× points on all dining activity',
      'Earn 15,000 points/month to unlock credits',
      'Monthly Explorer bonus points on top of your regular earnings',
      'Early access to new features and experiments',
      'Cancel anytime',
    ],
    lockMonths: 3,
    monthlyCapDollars: 10,
    monthlyGoalPoints: 15000,
    payoutCadence: 'every_2_months',
  },
  {
    planName: 'vip',
    name: 'VIP',
    price: '$14.99 / month',
    tag: 'For super food lovers',
    description: 'Maximize your points and unlock the highest earning potential.',
    features: [
      'Everything in Explorer',
      'Rewards unlock after 2 months of activity',
      'Earn up to $20 every 2 months',
      'Earn 5× points on all dining activity',
      'Earn 10,000 points/month to unlock credits',
      'VIP rollover: carry up to 2,000 qualification points into your next payout cycle',
      'VIP bonus drops with extra points during special promos',
      'Exclusive access to high-value member challenges',
      'Cancel anytime',
    ],
    lockMonths: 2,
    monthlyCapDollars: 20,
    monthlyGoalPoints: 10000,
    payoutCadence: 'every_2_months',
    rolloverPointsCap: 2000,
    rolloverApplies: true,
  },
]

/**
 * Gets the tier configuration for a given plan name
 * 
 * @param planName - The plan name ('free' | 'explorer' | 'vip' | null)
 * @returns The tier configuration, or Free tier as fallback if planName is null or not found
 */
export function getTierConfig(planName: PlanName | null): SubscriptionTier {
  if (planName === null) {
    return SUBSCRIPTION_TIERS[0] // Free tier as fallback
  }
  
  const tier = SUBSCRIPTION_TIERS.find(t => t.planName === planName)
  return tier ?? SUBSCRIPTION_TIERS[0] // Free tier as fallback if not found
}
